import React, { useState, useEffect } from 'react';
import { CheckCircle, ArrowRight, Loader2 } from 'lucide-react';
import SEO from '../components/SEO';
import sms360 from '../lib/sms360';

const SizeGuide = () => {
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    sms360.getAvailability()
      .then((data) => setAvailability(data))
      .catch(() => setAvailability(null))
      .finally(() => setLoading(false));
  }, []);

  const getUnitInfo = (unitType) => {
    if (!availability) return null;
    return availability.unit_types.find((u) => u.unit_type === unitType);
  };

  const standard = getUnitInfo('12x30');
  const large = getUnitInfo('14x40');

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Storage Sizes & Rates | VD Storage Bells"
        description="View available unit sizes and pricing for self storage, boat parking, and commercial yard storage in Bells, TX."
        canonical="/storage-sizes"
      />
      {/* Header */}
      <section className="bg-green-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="size-guide-title">
            Storage Size Guide
          </h1>
          <p className="text-xl text-green-100">
            Gravel lots perfect for boats, RVs, and equipment
          </p>
          {availability && (
            <p className="mt-3 text-lg font-semibold bg-green-700 inline-block px-4 py-2 rounded-full">
              {availability.total_available} spaces available
            </p>
          )}
        </div>
      </section>

      {/* Sizes */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading && (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 text-green-600 animate-spin" />
            </div>
          )}
          <div className="space-y-8">
            {/* 12x30 Space */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
                <div className="h-64 md:h-auto bg-gradient-to-br from-green-500 to-green-700 flex items-center justify-center">
                  <div className="text-center text-white p-8">
                    <h3 className="text-5xl font-bold mb-2">12' x 30'</h3>
                    <p className="text-xl">360 sq ft</p>
                    {standard && (
                      <div className="mt-4">
                        <p className="text-3xl font-bold">${standard.monthly_rate}<span className="text-lg">/mo</span></p>
                        <p className="mt-1 text-green-200 font-semibold">
                          {standard.available > 0 ? `${standard.available} available` : 'Waitlist'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-span-2 p-8">
                  <h4 className="text-2xl font-bold text-gray-900 mb-4">Standard Space</h4>
                  <p className="text-gray-700 mb-6 text-lg">
                    Perfect for most boats, single RVs, work trucks, and small equipment trailers.
                  </p>
                  <h5 className="font-semibold text-gray-900 mb-3">Ideal For:</h5>
                  <ul className="space-y-2 mb-6">
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Bass boats up to 21 feet</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Travel trailers and small RVs</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Work trucks and vans</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Small equipment and trailers</span>
                    </li>
                  </ul>
                  <a
                    href="/contact"
                    className={`inline-flex items-center px-6 py-3 text-base font-medium text-white rounded-lg transition-colors ${
                      standard && standard.available > 0
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {standard && standard.available > 0 ? 'Reserve Now' : 'Join Waitlist'}
                  </a>
                </div>
              </div>
            </div>

            {/* 14x40 Space */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-green-500">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
                <div className="h-64 md:h-auto bg-gradient-to-br from-green-600 to-green-800 flex items-center justify-center">
                  <div className="text-center text-white p-8">
                    <h3 className="text-5xl font-bold mb-2">14' x 40'</h3>
                    <p className="text-xl">560 sq ft</p>
                    <span className="inline-block mt-3 px-3 py-1 bg-white text-green-600 rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                    {large && (
                      <div className="mt-4">
                        <p className="text-3xl font-bold">${large.monthly_rate}<span className="text-lg">/mo</span></p>
                        <p className="mt-1 text-green-200 font-semibold">
                          {large.available > 0 ? `${large.available} available` : 'Waitlist'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-span-2 p-8">
                  <h4 className="text-2xl font-bold text-gray-900 mb-4">Large Space</h4>
                  <p className="text-gray-700 mb-6 text-lg">
                    Extra room for larger boats, big RVs, multiple vehicles, or equipment storage.
                  </p>
                  <h5 className="font-semibold text-gray-900 mb-3">Ideal For:</h5>
                  <ul className="space-y-2 mb-6">
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Boats 22-28 feet</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Large RVs and fifth wheels</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Multiple vehicles or trailers</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Construction equipment</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Business vehicle fleets</span>
                    </li>
                  </ul>
                  <a
                    href="/contact"
                    className={`inline-flex items-center px-6 py-3 text-base font-medium text-white rounded-lg transition-colors ${
                      large && large.available > 0
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {large && large.available > 0 ? 'Reserve Now' : 'Join Waitlist'}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            All Spaces Include
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start">
              <CheckCircle className="h-6 w-6 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-gray-900">24/7 Access</p>
                <p className="text-gray-600 text-sm">Come and go on your schedule</p>
              </div>
            </div>
            <div className="flex items-start">
              <CheckCircle className="h-6 w-6 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-gray-900">Gated & Secure</p>
                <p className="text-gray-600 text-sm">Fenced perimeter with secure gate</p>
              </div>
            </div>
            <div className="flex items-start">
              <CheckCircle className="h-6 w-6 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-gray-900">Gravel Surface</p>
                <p className="text-gray-600 text-sm">All-weather access</p>
              </div>
            </div>
            <div className="flex items-start">
              <CheckCircle className="h-6 w-6 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-gray-900">Wide Aisles</p>
                <p className="text-gray-600 text-sm">Easy maneuvering for large vehicles</p>
              </div>
            </div>
            <div className="flex items-start">
              <CheckCircle className="h-6 w-6 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-gray-900">Month-to-Month</p>
                <p className="text-gray-600 text-sm">No long-term contracts</p>
              </div>
            </div>
            <div className="flex items-start">
              <CheckCircle className="h-6 w-6 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-gray-900">Highway Access</p>
                <p className="text-gray-600 text-sm">Right on Highway 82</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-green-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Need Help Choosing a Size?</h2>
          <p className="text-xl text-green-100 mb-8">
            Contact us and we'll help you find the perfect space for your needs.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center px-8 py-3 text-lg font-semibold text-green-600 bg-white rounded-lg hover:bg-gray-100 transition-colors shadow-lg"
            data-testid="cta-button"
          >
            Contact Us Today
            <ArrowRight className="ml-2 h-5 w-5" />
          </a>
        </div>
      </section>
    </div>
  );
};

export default SizeGuide;
